'use client';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faCheck,
  faCube,
  faImageLandscape,
  faLock,
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box } from '@mantine/core';
import { assignInlineVars } from '@vanilla-extract/dynamic';

import type { CardLayoutKind, ImageField } from '../../_lib/types';

type SharedImages = {
  profilePicture: ImageField | null;
  coverPhoto: ImageField | null;
  companyLogo: ImageField | null;
};

import {
  bannerFill,
  bannerImage,
  bannerPlaceholderFill,
  bodyContainer,
  bodyRow,
  disabledOverlay,
  floatingImage,
  floatingLeftContainer,
  floatingRightContainer,
  floatingRightImage,
  layoutColorVar,
  placeholderIcon,
  selectedBadge,
  slideHeader,
  slideInner,
  slideRoot,
  slideRootSelected,
} from './LayoutSwitcherSlide.css';

type ImageType = 'profile' | 'logo' | 'cover';

type ImageSlot = {
  type: ImageType;
  url: string | null;
};

type ImageSlotResult = {
  banner?: ImageSlot;
  floatingLeft?: ImageSlot;
  floatingRight?: ImageSlot;
  isFloatingLeftCircular?: boolean;
};

const ICON_BY_IMAGE_TYPE: Record<ImageType, IconDefinition> = {
  profile: faUser,
  logo: faCube,
  cover: faImageLandscape,
};

function imageUrlFor(image: ImageField | null): string | null {
  return image?.editedUrl ?? image?.originalUrl ?? null;
}

function resolveImageSlots(layout: CardLayoutKind, images: SharedImages): ImageSlotResult {
  const profile: ImageSlot = { type: 'profile', url: imageUrlFor(images.profilePicture) };
  const logo: ImageSlot = { type: 'logo', url: imageUrlFor(images.companyLogo) };
  const cover: ImageSlot = { type: 'cover', url: imageUrlFor(images.coverPhoto) };

  switch (layout) {
    case '1A':
      return { banner: profile };
    case '1B':
      return { banner: logo };
    case '1C':
      return { banner: cover };
    case '2A':
      return { banner: profile, floatingLeft: logo };
    case '2B':
      return { banner: logo, floatingLeft: profile, isFloatingLeftCircular: true };
    case '2C':
      return { banner: cover, floatingLeft: profile, isFloatingLeftCircular: true };
    case '2D':
      return { banner: cover, floatingLeft: logo };
    case '3A':
      return {
        banner: cover,
        floatingLeft: profile,
        isFloatingLeftCircular: true,
        floatingRight: logo,
      };
    case 'blinq-logo':
    default:
      return {};
  }
}

function PlaceholderFill({ imageType, className }: { imageType: ImageType; className: string }) {
  return (
    <div className={className}>
      <div className={placeholderIcon}>
        <FontAwesomeIcon icon={ICON_BY_IMAGE_TYPE[imageType]} />
      </div>
    </div>
  );
}

type LayoutSwitcherSlideProps = {
  cardColor: string;
  disabled?: boolean;
  images: SharedImages;
  isSelected: boolean;
  layout: CardLayoutKind;
  onSelect: (layout: CardLayoutKind) => void;
  /** Why the tile is disabled, read out alongside its name. */
  disabledReason?: string | null;
};

export function LayoutSwitcherSlide({
  cardColor,
  disabled = false,
  images,
  isSelected,
  layout,
  onSelect,
  disabledReason,
}: LayoutSwitcherSlideProps) {
  const slots = resolveImageSlots(layout, images);
  const isBlinqLogo = layout === 'blinq-logo';
  const floatingLeftShape = slots.isFloatingLeftCircular ? 'circle' : 'rectangle';

  return (
    <Box
      className={`${slideRoot} ${isSelected ? slideRootSelected : ''}`}
      component="button"
      aria-label={disabledReason ? `Layout ${layout}. ${disabledReason}` : `Layout ${layout}`}
      aria-pressed={isSelected}
      disabled={disabled}
      onClick={() => {
        if (!disabled) {
          onSelect(layout);
        }
      }}
      style={assignInlineVars({
        [layoutColorVar]: cardColor,
      })}
      type="button"
    >
      {disabled && <div className={disabledOverlay} />}
      {isSelected && (
        <div className={selectedBadge}>
          {/* A locked (e.g. template-provided) layout shows a lock in place of
              the selection check. */}
          <FontAwesomeIcon icon={disabled ? faLock : faCheck} style={{ marginTop: '-2px' }} />
        </div>
      )}
      <div className={slideInner}>
        <div className={slideHeader({ layout })}>
          {isBlinqLogo ? (
            <div className={bannerFill} />
          ) : (
            <>
              {slots.banner?.url ? (
                <img alt="" className={bannerImage} src={slots.banner.url} />
              ) : (
                <PlaceholderFill
                  className={bannerPlaceholderFill}
                  imageType={slots.banner?.type ?? 'cover'}
                />
              )}
            </>
          )}

          {slots.floatingLeft && (
            <div className={floatingLeftContainer({ shape: floatingLeftShape })}>
              {slots.floatingLeft.url ? (
                <img
                  alt=""
                  className={floatingImage({ shape: floatingLeftShape })}
                  src={slots.floatingLeft.url}
                />
              ) : (
                <PlaceholderFill
                  className={floatingImage({ shape: floatingLeftShape })}
                  imageType={slots.floatingLeft.type}
                />
              )}
            </div>
          )}

          {slots.floatingRight && (
            <div className={floatingRightContainer}>
              {slots.floatingRight.url ? (
                <img alt="" className={floatingRightImage} src={slots.floatingRight.url} />
              ) : (
                <PlaceholderFill
                  className={floatingRightImage}
                  imageType={slots.floatingRight.type}
                />
              )}
            </div>
          )}
        </div>

        <div className={bodyContainer}>
          <div className={bodyRow({ width: 'full' })} />
          <div className={bodyRow({ width: 'medium' })} />
          <div className={bodyRow({ width: 'short' })} />
        </div>
      </div>
    </Box>
  );
}
